/* eslint-disable array-callback-return */
import {
    Accidental,
    Flow,
    FormatParams,
    Formatter,
    RenderContext,
    Renderer,
    TickContext,
    Voice,
} from 'vexflow4';
import { Position, TouchListener } from './singleTouchListener';
import { NoteMap } from './noteMap';
import {
    checkCurrentNote,
    findWhichStaveMouseOver,
    formatStavesToJson, getAccidentals, getMoveKeysFunc, transposePitch, uniqueStringArray,
} from '../utils/helpers';
import { PROVISORY_TICKABLE_STYLE } from './constants';
import { StaveNote } from './extension/MusiconStaveNote';
import Stave from './extension/MusiconStave';
import Player, { ClefType, MidiStruct, PlayInfo } from "./player";
import { BarNote } from "./extension/MusiconBarNote";
import { Note } from "tonal";

const MELODY_KEY_RANGE = { max: 'G/6', min: 'B/2' };
const HEAD_STAVE_WIDTH = localStorage.getItem('question_type') === 'YinJie' ? 40 : 140;
const PADDING_HORIZONTAL = 5;
const PADDING_TOP = 20;
const START_OF_STAVE = HEAD_STAVE_WIDTH + PADDING_HORIZONTAL;
const CONTENT_STAVE_WIDTH = window.innerWidth - START_OF_STAVE - PADDING_HORIZONTAL - PADDING_HORIZONTAL;

const MIDI: MidiStruct = window.MIDI as unknown as MidiStruct;

export default class SingleStaveHandler {
    public static instance: SingleStaveHandler | null = null;
    private render: Renderer;
    private ctx: RenderContext;
    private _stave: Stave;
    private provisoryTickable: StaveNote | null = null;
    public duration: string = '4';
    private dragCursorX: number | null = null;
    private currentNoteCursorX: number | null = null;
    private currentBarIndex: number | undefined = undefined;
    private preProvisoryTickable: StaveNote | null = null;
    private melodyHeadStave: Stave;

    private noteUpStatus: number = 0;
    private noteDownStatus: number = 0;

    public currentStave: Stave | null = null;
    public currentNote: StaveNote | null = null;

    public playing: boolean = false;
    private player: Player;

    private head_stave_width;
    private padding_horizontal;
    private padding_top;
    private start_of_stave;
    private content_stave_width;

    public constructor(render: Renderer) {
        const questionType = localStorage.getItem('question_type');
        this.head_stave_width = questionType === 'YinJie' ? 20 : 140;
        this.padding_horizontal = 5;
        this.padding_top = 20;
        this.start_of_stave = this.head_stave_width + this.padding_horizontal;
        this.content_stave_width = window.innerWidth - this.start_of_stave - this.padding_horizontal * 2;

        this.melodyHeadStave = new Stave(this.padding_horizontal, this.padding_top, this.head_stave_width);

        this.render = render;
        const width = this.render;
        this.ctx = render.getContext();
        const stave = new Stave(this.start_of_stave, this.padding_top, this.content_stave_width);
        stave.setBegBarType(7);
        this._stave = stave;
        this.currentStave = stave;
        this.player = new Player(this);
    }

    public resize = (width: number, height: number = 200) => {
        this.ctx.resize(width, height);
        this._stave.setWidth(width - this.start_of_stave - this.padding_horizontal * 2);
        this.redraw();
    }

    public stop = () => {
        this.player.stop();
        this.playing = false;
    };

    public play = (callback?: () => void) => {
        if (this.playing) return;

        this.playing = true;

        const onFinished = () => {
            this.playing = false;
        };

        const melodyPlayer = this.playStaves(this.player, [this.stave], 'treble', () => {
            onFinished();
            callback && callback();
        });

        setTimeout(() => {
            melodyPlayer.play();
        }, 300);
    };

    private playStaves = (player: Player, staves: Stave[], clef: ClefType, callback: () => void) => {
        const rpm = 120;
        const playInfo: PlayInfo = {
            delay: 0,
            rpm: rpm,
            defaultTime: rpm / 60, // to seconds
            clef,
        };
        let playEvents: any[] = [];
        staves.map((stave: Stave) => {
            playInfo.clef = clef;
            const barNote = new BarNote();
            stave.getTickables().map((note: StaveNote) => {
                playEvents = playEvents.concat(note.getPlayEvents(playInfo));
            });
            playEvents = playEvents.concat(barNote.getPlayEvents(playInfo, playEvents));
        });
        player.addEvents(playEvents);
        player.onPlayFinished(() => {
            callback();
        });
        return player;
    };

    private redrawCallback: (data: string) => void = (data: string) => {
    };

    private noteDoubleUpStatus: number = 0;
    public afterInsertNoteDoubleUpStatusCallback: () => void = () => {
    };

    private noteDoubleDownStatus: number = 0;
    public afterInsertNoteDoubleDownStatusCallback: () => void = () => {
    };

    private renoteStatus: number = 0;
    public afterInsertRenoteStatusCallback: () => void = () => {
    };
    public getNoteDoubleUpStatus = (): number => this.noteDoubleUpStatus;
    public setNoteDoubleUpStatus = (status: number) => {
        this.noteDoubleUpStatus = status;
    };

    public getNoteDoubleDownStatus = (): number => this.noteDoubleDownStatus;
    public setNoteDoubleDownStatus = (status: number) => {
        this.noteDoubleDownStatus = status;
    };

    public getRenoteStatus = (): number => this.renoteStatus;
    public setRenoteStatus = (status: number) => {
        this.renoteStatus = status;
    };

    public static getInstance(render: Renderer): SingleStaveHandler {
        if (!SingleStaveHandler.instance) SingleStaveHandler.instance = new SingleStaveHandler(render);
        return SingleStaveHandler.instance;
    }

    public setRedrawCallback = (callback: (data: string) => void) => {
        this.redrawCallback = callback;
    }

    public get stave(): Stave {
        return this._stave;
    }

    public set stave(stave: Stave) {
        this._stave = stave;
    }

    public init = () => {
        this.ctx.clear();
        this.redraw();
    };

    public redraw = () => {
        const data = JSON.stringify(formatStavesToJson([this.stave]));
        this.redrawCallback(data);

        this.ctx.clear();
        this._drawHeads();
        this._drawDragCursor();
        // this._drawNoteCursor();
        this._drawStaves();
        this._drawNotes();
        this._drawProvisoryTickable();
    };

    public getStaveList: () => Stave[] = () => ([this.stave]);
    public getCurrentStave = (): Stave | null => this.currentStave;
    public setCurrentStave = (stave: Stave | null) => {
        this.currentStave = stave;

        if (stave == null) {
            this.dragCursorX = null;
        }
    };
    public getCurrentNote = (): StaveNote | null => this.currentNote;
    public setCurrentNote = (note: StaveNote | null) => {
        this.currentNote = note;

        try {
            this.currentNoteCursorX = note?.getAbsoluteX() ?? null;
        } catch (e) {
        }
    };

    public getNoteUpStatus = (): number => this.noteUpStatus;
    public setNoteUpStatus = (status: number) => {
        this.noteUpStatus = status;
    };
    public getNoteDownStatus = (): number => this.noteDownStatus;
    public setNoteDownStatus = (status: number) => {
        this.noteDownStatus = status;
    };

    public getDragCursorX = (): number | null => this.dragCursorX;
    public setDragCursorX = (x: number | null) => {
        this.dragCursorX = x;
    };
    public isHeadStave = (pos: Position) => {
        return this.melodyHeadStave.getX() + this.melodyHeadStave.getWidth() >= pos.x;
    };

    public startListenTouchEvent(canvas: HTMLCanvasElement | null) {
        if (!canvas) return;
        const listener = new TouchListener(canvas, this);
        listener.startListening();
    }

    public stopListenTouchEvent = (canvas: HTMLCanvasElement | null) => {
        if (!canvas) return;
        const listener = new TouchListener(canvas, this);
        listener.stopListening();
    };

    public getNextStave = (stave: Stave) => {
        const box = stave.getBoundingBox();

        const mousePos = {
            x: box.x + box.w + 1,
            y: stave.getTopLineTopY(),
        };

        const next = findWhichStaveMouseOver([this.stave], mousePos);

        return next;
    };

    private _drawHeads = () => {
        const mhs = new Stave(this.padding_horizontal, this.padding_top, this.head_stave_width);
        const yincheng_tone = localStorage.getItem('yincheng_tone') || 'C';
        mhs.addClef('treble');

        mhs.setEndBarType(7);
        mhs.setKeySignature(yincheng_tone);

        mhs.setContext(this.ctx).draw();
        this.melodyHeadStave = mhs;
    };

    private _drawDragCursor = () => {
        try {

            if (!this.dragCursorX || !this.currentStave) {
                return;
            }

            const tempFillStyle = this.ctx.fillStyle;
            this.ctx.fillStyle = 'rgba(255, 241, 212, 0.80)';

            const space = this.currentStave.getSpacingBetweenLines();
            const x = this.dragCursorX - space / 2;
            const y = 0;
            const w = space * 2;
            const h = 240;

            this.ctx.fillRect(x, y, w, h);

            this.ctx.fillStyle = tempFillStyle;
        } catch (e) {
            console.log('>>> _drawDragCursor error: ', e);
        }
    };

    private _drawNoteCursor = () => {
        try {
            const x = this.currentNoteCursorX;
            const stave = this.currentStave;

            this.currentBarIndex = undefined;

            if (!stave || !x) return;

            const space = stave.getSpacingBetweenLines();
            const y = stave.getTopLineTopY();
            const w = space * 2;
            const h = space * 4;

            const tempFillStyle = this.ctx.fillStyle;

            this.ctx.fillStyle = '#FDE3AF';
            this.ctx.fillRect(x - space / 2, y, w, h);
            // 找到当前选中的小节编号
            if (this.currentStave?.getY() === 20) {
                this.currentBarIndex = (this.currentStave?.getX() - 135) / 150;
            }

            this.ctx.fillStyle = tempFillStyle;
        } catch (e) {
            console.log('>>> _drawNoteCursor error: ', e);
        }
    };

    private _drawStaves = () => {
        try {
            [this.stave].forEach((stave: Stave, i: number) => {
                if (stave instanceof Stave) {
                    stave.setContext(this.ctx).draw();
                }
            });
        } catch (e) {
            console.log('>>> _drawStaves error: ', e);
        }
    };

    private _drawNotes = (stave?: Stave) => {
        try {

            if (stave === undefined) {
                [this.stave].forEach((stave) => {
                    this._drawNotes(stave);
                });
            } else {
                const voice = new Voice(Flow.TIME4_4).setMode(Voice.Mode.SOFT);
                const notes = stave.getTickables().map((note: StaveNote) => {
                    note.setCenterAlignment(false);
                    note.setWidth(25);
                    return note;
                });
                voice.addTickables(notes);

                const formatter = new Formatter();
                const formatOption = {
                    align_rests: true,
                    auto_beam: true,
                    width: 50,
                    minSpace: 50,
                } as FormatParams;

                const questionType = localStorage.getItem('question_type');
                if (questionType === 'YinJie') {
                    // 音阶题动态调整渲染note时候stave的宽度，为了让note可以整体居左显示
                    const staveWidth = stave.getTickables().length * 30 - 20;
                    const width = staveWidth < this.content_stave_width ? staveWidth : this.content_stave_width;
                    formatter.joinVoices([voice]).format([voice], width);
                } else {
                    formatter.joinVoices([voice]);

                    if (stave.getTickables().length > 0) {
                        formatter.formatToStave([voice], stave, formatOption);
                    }
                }
                voice.draw(this.ctx, stave);
            }
        } catch (e) {
            console.log('>>> _drawNotes error: ', e)
        }
    };

    private _drawProvisoryTickable = () => {
        try {
            if (!this.currentStave) return;
            if (!this.provisoryTickable) return;

            //Only draw Provisory note if not on a definitive note
            const tempFillStyle = this.ctx.fillStyle;
            const tempStrokeStyle = this.ctx.strokeStyle;
            this.ctx.fillStyle = 'gray';
            this.ctx.strokeStyle = 'gray';

            this.provisoryTickable.setStave(this.currentStave);
            this.provisoryTickable.draw();

            this.ctx.fillStyle = tempFillStyle;
            this.ctx.strokeStyle = tempStrokeStyle;
        } catch (e) {
            console.log('>>> _drawProvisoryTickable error: ', e)
        }
    };

    public updateProvisoryKey = (mousePos: Position) => {
        this.dragCursorX = mousePos.x;
        if (this.currentStave == null) {
            return;
        }

        if (this.provisoryTickable == null) {
            let newNote = new StaveNote({ keys: ['d/4'], duration: this.duration });

            this.setProvisoryTickable(newNote);
        }

        if (
            !(this.provisoryTickable instanceof StaveNote) ||
            this.provisoryTickable?.getNoteType() === 'r'
        ) {
            //No need to update key if its not a note or if its a rest, just draw the tickable in the new mouse position
        } else {
            const noteName = NoteMap.getNoteName(this.currentStave, mousePos);

            if (noteName !== this.provisoryTickable.getKeys()[0]) {
                const cloneNote = this.provisoryTickable.clone({
                    keys: [noteName],
                });
                this.setProvisoryTickable(cloneNote);

                if (this.provisoryTickable) {
                    this.provisoryTickable.setStyle(PROVISORY_TICKABLE_STYLE);
                }
                this.provisoryTickable?.autoStem();
                this.provisoryTickable?.setStave(this.currentStave);
                this.provisoryTickable?.setTickContext(new TickContext());
            }
        }
        this.drawProvisoryTickable(mousePos);
    };
    public setProvisoryTickable = (note: StaveNote | null) => {
        this.preProvisoryTickable = this.provisoryTickable;
        this.provisoryTickable = note;
    };
    public getLastTickableBeforeXPosition = (x: number) => {
        return this._findNearStaveNote(x, true);
    };
    public getFirstTickableAfterXPosition = (x: number) => {
        return this._findNearStaveNote(x, false);
    };

    private _findNearStaveNote = (x: number, isPre: boolean = true) => {
        const stave = this.currentStave;
        const staveNotes = stave?.getTickables() ?? [];

        if (staveNotes.length === 0) {
            return null;
        }

        let nearNotes;

        if (isPre) {
            nearNotes = staveNotes.filter((n) => n.getAbsoluteX() < x);
            nearNotes.sort((a: StaveNote, b: StaveNote) => {
                return a.getAbsoluteX() - b.getAbsoluteX() < 0 ? 1 : -1;
            });
        } else {
            nearNotes = staveNotes.filter((n) => n.getAbsoluteX() > x);
            nearNotes.sort((a: StaveNote, b: StaveNote) => {
                return a.getAbsoluteX() - b.getAbsoluteX() > 0 ? 1 : -1;
            });
        }

        return nearNotes[0] ?? null;
    };
    public afterInsertNoteUpStatusCallback: () => void = () => {
    };
    public getProvisoryTickable = (): StaveNote | null => this.preProvisoryTickable;
    public drawProvisoryTickable = (mousePos: Position) => {
        if (this.currentStave == null || this.provisoryTickable == null || !mousePos) return;

        if (this.provisoryTickable instanceof StaveNote) {
            //Fix X position to set exactly where the mouse is
            //TODO the -5 value shouldnt be absolute! it should reflect half the note's Width
            const xShift = mousePos.x - this.provisoryTickable.getAbsoluteX() - 5;
            this.provisoryTickable.setXShift(xShift);
        }

        this.dragCursorX = mousePos.x;

        this.redraw();
    };
    public afterInsertNoteDownStatusCallback: () => void = () => {
    };

    public getStaveNotes = () => {
        const notes = formatStavesToJson([this.stave]);

        console.log('>>> notes: ', notes);
    }

    public setStaveNotes = (notes: any[]) => {
        const newNotes = notes.map(
            ({
                keys,
                duration,
                accidentals,
            }: {
                keys: string[];
                duration: string;
                accidentals: string[];
            }) => {
                const note = new StaveNote({ keys, duration });

                note.autoStem();

                accidentals.map((accidental: any): void => {
                    if (accidental === null) {
                        return;
                    }

                    if (typeof accidental === 'string') {
                        note.addModifier(new Accidental(accidental), 0);
                        return;
                    }

                    const index = accidental.index;

                    note.addModifier(new Accidental(accidental.modifier), index);
                });

                return note;
            },
        );
        this.stave.setTickables(newNotes);

        this.redraw();
    }

    public downAllKeys = (defaultNote: string) => {
        this.stave = moveKeysFunc('down', this.currentNote, defaultNote)(this.stave);
        this.redraw();
    };

    public upAllKeys = (defaultNote: string) => {
        this.stave = moveKeysFunc('up', this.currentNote, defaultNote)(this.stave);
        this.redraw();
    }

    public playNotes = (notes: string[], cb?: () => void) => {
        // 在无渲染的时候，直接播放声音
        const playEvents: any[] = [];
        const playNotes: any[] = [];
        const repeatNotes: any[] = [];
        let queuedTime = 0;

        notes = ['A/4'].concat(notes);
        notes.forEach(((note: string, index: number) => {
            const noteObj = Note.get(note.replace('/', ''));
            if (index === 0) {
                playNotes.push([noteObj.midi])
            } else {
                if (playNotes.length === 1) {
                    playNotes.push([noteObj.midi])
                } else {
                    playNotes[1].push(noteObj.midi)
                }
                repeatNotes.push(noteObj.midi);
            }
        }));
        // 和弦音符重复播放三遍
        for (let i = 0; i < 2; i++) {
            playNotes.push(repeatNotes);
        }

        this.playing = true;
        playNotes.forEach(item => {
            playEvents.push({
                type: 'channel',
                channel: 0,
                subtype: item.length === 1 ? 'noteOn' : 'chordOn',
                noteNumber: item.length === 1 ? item[0] : item,
                velocity: 127,
                queuedTime,
            });
            playEvents.push({
                type: 'channel',
                channel: 0,
                subtype: item.length === 1 ? 'noteOff' : 'chordOff',
                noteNumber: item.length === 1 ? item[0] : item,
                queuedTime: queuedTime + 3,
            });
            queuedTime += 3;
        })
        this.player.addEvents(playEvents);
        this.player.onPlayFinished(() => {
            this.playing = false;
            cb && cb()
        });
        setTimeout(() => { this.player.play() }, 1000);
    }
}

const moveKeysFunc = (action: string, currentNote: StaveNote | null, defaultNote: string) => (stave: Stave) => {
    const notes = stave.getTickables();
    const newNotes = notes.map((note: StaveNote) => {
        if (!checkCurrentNote(note, currentNote)) {
            return note;
        }
        const newKeys = note.getKeys().map((key: string) => {
            return key !== defaultNote ? transposePitch(key, action, MELODY_KEY_RANGE) : key;
        });

        note.keys = uniqueStringArray(newKeys);

        const newNote = new StaveNote({
            keys: newKeys,
            duration: note.getDuration(),
        });

        const stave = note.getStave();

        if (stave) newNote.setStave(stave);

        getAccidentals(note).forEach((accidental) => {
            newNote.addModifier(new Accidental(accidental?.modifier || accidental), accidental.index);
        });

        return newNote;
    });

    stave.setTickables(newNotes);
    return stave;
}
